import { canonicalSync } from "./syncV2.js";
import type { CalendarEvent, CalendarLessonArrangement } from "./types.js";

export interface SchoolCalendarSelection {
  start: string;
  end: string;
  anchor: string | null;
}

export interface SchoolCalendarChange {
  before?: CalendarEvent;
  after?: CalendarEvent;
}

function validDate(date: string) {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    Number.isFinite(Date.parse(date)) &&
    new Date(date).toISOString().slice(0, 10) === date
  );
}

export function schoolCalendarSelection(
  selected: SchoolCalendarSelection | null,
  date: string,
): SchoolCalendarSelection {
  if (!validDate(date)) throw new Error("invalid-calendar-date");
  const anchor = selected?.anchor;
  return anchor
    ? { start: anchor < date ? anchor : date, end: anchor > date ? anchor : date, anchor: null }
    : { start: date, end: date, anchor: date };
}

export function isValidLessonArrangement(value: unknown): value is CalendarLessonArrangement | undefined {
  return value === undefined || value === "normal" || value === "noClasses" || value === "special";
}

export function calendarLessonArrangement(event: CalendarEvent): CalendarLessonArrangement | undefined {
  if (event.type !== "school") return undefined;
  return (
    event.lessonArrangement ??
    (event.schoolKind === "publicHoliday" || event.schoolKind === "schoolHoliday" ? "noClasses" : undefined)
  );
}

export function isSchoolCalendarEvent(event: CalendarEvent) {
  return (
    (event.type === "school" || (event.type === "reminder" && !event.classId)) && event.source?.kind !== "official"
  );
}

function eventVersion(event: CalendarEvent | undefined) {
  // Match JSON persistence, which omits optional properties set to undefined.
  return canonicalSync(JSON.parse(JSON.stringify(event ?? null)));
}

export function schoolCalendarChanges(before: CalendarEvent[], after: CalendarEvent[]): SchoolCalendarChange[] {
  const old = new Map(before.map((event) => [event.id, event]));
  const next = new Map(after.map((event) => [event.id, event]));
  return [...new Set([...old.keys(), ...next.keys()])].flatMap((id) => {
    const previous = old.get(id),
      current = next.get(id);
    return eventVersion(previous) === eventVersion(current) ? [] : [{ before: previous, after: current }];
  });
}

/** Apply only the edited rows; unrelated edits in another tab must survive. */
export function applySchoolCalendarChanges(current: CalendarEvent[], changes: SchoolCalendarChange[]): CalendarEvent[] {
  const events = new Map(current.map((event) => [event.id, event]));
  const ids = new Set<string>();
  for (const change of changes) {
    const { before, after } = change;
    const id = before?.id ?? after?.id;
    if (
      !id ||
      ids.has(id) ||
      (before && after && before.id !== after.id) ||
      (before && !isSchoolCalendarEvent(before)) ||
      (after && !isSchoolCalendarEvent(after))
    )
      throw new Error("invalid-school-calendar-change");
    ids.add(id);
    const actual = events.get(id);
    if (eventVersion(actual) !== eventVersion(before)) throw new Error("school-calendar-conflict");
    if (after) {
      if (
        !after.title.trim() ||
        !validDate(after.date) ||
        (after.endDate !== undefined && (!validDate(after.endDate) || after.endDate < after.date)) ||
        !isValidLessonArrangement(after.lessonArrangement) ||
        (before?.workspaceId && before.workspaceId !== after.workspaceId)
      )
        throw new Error("invalid-school-calendar-change");
      events.set(id, after);
    } else events.delete(id);
  }
  return [...events.values()];
}
